import "bootstrap/dist/css/bootstrap.css";
import "react-loading-skeleton/dist/skeleton.css";
import "react-calendar-heatmap/dist/styles.css";

import { ConfigProvider, message, theme as antdTheme } from "antd";
import { SkeletonTheme } from "react-loading-skeleton";
import React, { Suspense, useEffect, useLayoutEffect, useState } from "react";

import { Provider, useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import Routes from "./Routes";
import { store, persistor } from "./store/store";

import { QueryClient, QueryClientProvider, useMutation } from "react-query";
import fa_IR from "antd/es/locale/fa_IR";

import { setUpInterceptorStore } from "./utils/API";

import { OrgSwitchSplash, SplashScreen } from "./components";
import { ScrollToTop } from "./utils/ScrollToTop";
import useServiceWorker from "./hooks/useServiceWorker";
import UpdateAppModal from "./components/UpdateAppModal";
import { useMediaQuery } from "./hooks";
import {
  getAccentColor,
  getAccentForeground,
  getAccentRgb,
} from "./utils/helpers";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
  },
});

const App = () => {
  const { waitingWorker, showReload, reloadPage } = useServiceWorker();

  const [showUpdateModal, setShowUpateModal] = useState(false);

  useEffect(() => {
    setUpInterceptorStore(store);
  }, []);

  useEffect(() => {
    if (showReload && waitingWorker) {
      setShowUpateModal(true);
    }
  }, [waitingWorker, showReload, reloadPage]);

  return (

    <Provider store={store}>
      {}
      <PersistGate persistor={persistor} loading={<SplashScreen />}>
        {}
        <Suspense fallback={<SplashScreen />}>
          {}
          <QueryClientProvider client={queryClient}>
            {}

            <AntdProvider>
              {}
              <BrowserRouter>
                <Routes />
                <ScrollToTop />
                {}
                <UpdateAppModal
                  isOpen={showUpdateModal}
                  setIsOpen={setShowUpateModal}
                  onUpdate={() => reloadPage()}
                />
              </BrowserRouter>
            </AntdProvider>
          </QueryClientProvider>
        </Suspense>
      </PersistGate>
    </Provider>
  );
};

const AntdProvider = ({ children }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const organization = useSelector(({ auth }) => auth.organization);
  const appTheme = useSelector(({ common }) => common.theme || "light");
  const isDark = appTheme === "dark";

  const accent = getAccentColor(organization);

  /* Layout effect, not a plain one: `useEffect` runs *after* the first paint,
   * so a dark-theme user got one frame of the light palette on every boot —
   * the token blocks key off this attribute. It is most obvious on the
   * workspace-switch splash, which is a full screen of colour and is the first
   * thing rendered after a switch. */
  useLayoutEffect(() => {
    document.body.setAttribute("data-theme", appTheme);

    document.documentElement.style.backgroundColor = isDark
      ? "#0f172a"
      : "#f5f7fc";
  }, [appTheme, isDark]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--ds-accent", accent);
    /* Readable text on a solid accent fill; see getAccentForeground. */
    root.style.setProperty(
      "--ds-accent-fg",
      getAccentForeground(organization),
    );
    root.style.setProperty("--bs-primary", accent);
    root.style.setProperty("--bs-primary-rgb", getAccentRgb(organization));
  }, [accent, organization]);

  const skeletonBase = isDark ? "#253347" : "#e2e8f0";
  const skeletonHighlight = isDark ? "#334155" : "#f1f5f9";

  return (
    <ConfigProvider
      prefixCls="ant"
      direction="rtl"
      locale={fa_IR}
      theme={{
        cssVar: { prefix: "ant" },
        algorithm: isDark
          ? antdTheme.darkAlgorithm
          : antdTheme.defaultAlgorithm,
        token: {
          fontFamily: "iranyekan",
          fontSize: isMobile ? 12 : 14,
          controlHeight: 32,
          colorPrimary: accent,
          colorInfo: accent,
          borderRadius: 8,
          borderRadiusLG: 12,
          borderRadiusSM: 6,
          borderRadiusXS: 4,

          colorBgLayout: isDark ? "#0f172a" : "#f5f7fc",
          colorBgContainer: isDark ? "#1e293b" : "#ffffff",
          colorBgElevated: isDark ? "#253347" : "#ffffff",
          colorBgSpotlight: isDark ? "#334155" : "#4a4a4a",

          colorBorder: isDark ? "#334155" : "#e2e8f0",
          colorBorderSecondary: isDark ? "#1e293b" : "#f1f5f9",

          colorText: isDark ? "#f1f5f9" : "#0f172a",
          colorTextSecondary: isDark ? "#94a3b8" : "#64748b",
          colorTextTertiary: isDark ? "#64748b" : "#94a3b8",
          colorTextQuaternary: isDark ? "#475569" : "#cbd5e1",

          colorTextPlaceholder: isDark ? "#64748b" : "#9ca3af",

          boxShadow: isDark
            ? "0 1px 3px 0 rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.2)"
            : "0 1px 3px 0 rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)",
          boxShadowSecondary: isDark
            ? "0 4px 16px 0 rgba(0,0,0,0.4)"
            : "0 4px 16px 0 rgba(0,0,0,0.08)",
        },
        components: {
          Card: {
            borderRadiusLG: 12,
            paddingLG: 20,
          },
          Menu: {
            itemBorderRadius: 8,
            itemMarginInline: 8,
            subMenuItemBorderRadius: 8,
            itemHeight: 38,
          },
          Table: {
            headerBg: isDark ? "#253347" : "#f8fafc",
            borderRadius: 12,
            borderRadiusLG: 12,
          },
          Statistic: {
            titleFontSize: 12,
          },
          Button: {
            borderRadius: 8,
          },

          Input: {
            borderRadius: 8,
            fontSizeLG: isMobile ? 12 : 14,
          },
          Select: {
            borderRadius: 8,
            fontSizeLG: isMobile ? 12 : 14,
          },
          DatePicker: {
            fontSizeLG: isMobile ? 12 : 14,
          },
          InputNumber: {
            fontSizeLG: isMobile ? 12 : 14,
          },
          Mentions: {
            fontSizeLG: isMobile ? 12 : 14,
          },
          Cascader: {
            fontSizeLG: isMobile ? 12 : 14,
          },
          TreeSelect: {
            fontSizeLG: isMobile ? 12 : 14,
          },
          Modal: {
            borderRadiusLG: 16,
          },
          Tag: {
            borderRadius: 6,
          },
        },
      }}
    >
      <SkeletonTheme
        baseColor={skeletonBase}
        highlightColor={skeletonHighlight}
      >
        {children}
        {/* Above the router rather than inside it: a workspace switch replaces
            the document, so the screen that covers it cannot belong to any one
            route. */}
        <OrgSwitchSplash />
      </SkeletonTheme>
    </ConfigProvider>
  );
};

export default App;
